import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import config from "./config";
import { resendVerificationEmail } from "./services/verificationApi";

function getTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") || "";
}

function getInviteTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("invite_token") || "";
}

export default function VerifyEmail({ onBack }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending"); // pending, success, error
  const [message, setMessage] = useState("");
  const [autoJoinedLeague, setAutoJoinedLeague] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const token = getTokenFromUrl();
  const inviteToken = getInviteTokenFromUrl();
  const hasVerifiedRef = useRef(false);

  const handleGoToLogin = () => {
    // Clear any stored invite tokens
    localStorage.removeItem('pendingInviteToken');
    
    // If user was auto-added to a league, redirect to that league
    if (autoJoinedLeague) {
      navigate(`/league/${autoJoinedLeague.id}`);
    } else if (onBack) {
      onBack();
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    let finished = false;
    
    async function verify() {
      if (hasVerifiedRef.current) return; // Prevent multiple verification attempts
      
      try {
        let url = `${config.API_URL}/verify-email?token=${token}`;
        if (inviteToken) {
          url += `&invite_token=${inviteToken}`;
        }
        
        const res = await fetch(url);
        const data = await res.json();
        
        if (!finished) {
          hasVerifiedRef.current = true;
          if (res.ok) {
            setStatus("success");
            setMessage(data.message || "Email verified successfully!");
            
            // Check if user was auto-added to a league
            if (data.auto_joined && data.league_id) {
              setAutoJoinedLeague({
                id: data.league_id,
                name: data.league_name
              });
            }
          } else if (data.detail && data.detail.toLowerCase().includes("already verified")) {
            setStatus("success");
            setMessage("Your email is already verified. You can log in.");
          } else {
            setStatus("error");
            setMessage(data.detail || "Verification failed or link expired.");
          }
        }
      } catch (err) {
        if (!finished) {
          hasVerifiedRef.current = true;
          setStatus("error");
          setMessage("Verification failed or link expired.");
        }
      }
    }
    
    if (token && !hasVerifiedRef.current) {
      verify();
    } else if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
    }
    
    return () => { finished = true; };
  }, [token, inviteToken]);

  const handleResend = async () => {
    const targetEmail = resendEmail.trim();
    if (!targetEmail) {
      setResendMsg("Enter the email address you used to register.");
      return;
    }
    setResendLoading(true);
    setResendMsg("");
    try {
      const message = await resendVerificationEmail(targetEmail);
      setResendMsg(message);
    } catch (err) {
      setResendMsg(err.message || "Could not resend verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{ padding: 40, maxWidth: 400, margin: "40px auto" }}>
      <h2 style={{ textAlign: "center", color: status === "success" ? "#059669" : "#dc2626" }}>
        {status === "success" ? "Email Verified" : "Verification Failed"}
      </h2>
      <p style={{ fontSize: 16, color: "#334155", marginBottom: 24, textAlign: "center" }}>{message}</p>

      {status === "error" && (
        <div style={{ marginBottom: 24, padding: 16, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
          <p style={{ fontSize: 14, color: "#334155", marginBottom: 8, fontWeight: 600 }}>
            Request a new verification link
          </p>
          <input
            type="email"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            placeholder="Email you registered with"
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 8,
              border: "1px solid #d1d5db",
              fontSize: 16,
              marginBottom: 8,
              boxSizing: "border-box",
            }}
            disabled={resendLoading}
          />
          {resendMsg && (
            <p style={{ fontSize: 14, color: resendMsg.includes("sent") ? "#16a34a" : "#dc2626", marginBottom: 8 }}>
              {resendMsg}
            </p>
          )}
          <button
            type="button"
            className="ffb-btn"
            style={{ width: "100%" }}
            disabled={resendLoading}
            onClick={handleResend}
          >
            {resendLoading ? "Sending..." : "Resend verification email"}
          </button>
        </div>
      )}
      
      {autoJoinedLeague && (
        <div style={{ 
          background: "#f0fdf4", 
          border: "1px solid #bbf7d0", 
          borderRadius: 8, 
          padding: 16, 
          marginBottom: 24,
          textAlign: "center"
        }}>
          <p style={{ color: "#059669", margin: 0, fontWeight: 600 }}>
            🎉 Welcome to {autoJoinedLeague.name}!
          </p>
        </div>
      )}
      
      <button className="ffb-btn" style={{ width: "100%" }} onClick={handleGoToLogin}>
        {autoJoinedLeague ? "Go to League" : "Go to Login"}
      </button>
    </div>
  );
} 